import { useState } from 'react';
import { useCoverageSweepRuns, useTriggerCoverageSweep } from './useHaloV2';

const cardCls =
  'rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 text-sm';

const STATE_COLORS: Record<string, string> = {
  completed: 'text-green-600',
  failed: 'text-red-600',
  active: 'text-blue-600',
  waiting: 'text-amber-600',
  delayed: 'text-amber-600',
};

const DEFAULT_CAP = 200;

export function CoverageSweepCard() {
  const [cap, setCap] = useState<number>(DEFAULT_CAP);
  const runsQuery = useCoverageSweepRuns();
  const trigger = useTriggerCoverageSweep();

  const onSweep = () => {
    if (
      window.confirm(
        `Trigger a coverage sweep across all stores (up to ${cap})? ` +
          `Enqueues one backfill per store with missing HaloV2 history.`,
      )
    ) {
      trigger.mutate({ cap });
    }
  };

  const latest = runsQuery.data?.[0];
  const summary = latest?.result;

  return (
    <div className={cardCls}>
      <h3 className="font-medium text-gray-700 dark:text-gray-200 mb-2">Backfill all stores</h3>
      <div className="flex items-center gap-2 mb-2">
        <label className="text-xs text-gray-500 dark:text-gray-400">
          max stores
          <input
            type="number"
            min={1}
            max={200}
            value={cap}
            onChange={(e) => {
              const n = Number(e.target.value);
              setCap(Number.isFinite(n) ? Math.min(200, Math.max(1, Math.trunc(n))) : DEFAULT_CAP);
            }}
            className="ml-1 w-16 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-xs"
          />
        </label>
        <button
          type="button"
          disabled={trigger.isPending}
          onClick={onSweep}
          className="rounded-md bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 px-3 py-1 text-xs font-medium disabled:opacity-50"
        >
          Backfill all stores
        </button>
      </div>
      {trigger.isError ? (
        <p className="text-xs text-red-500 mb-2">
          {trigger.error?.message ?? 'Coverage sweep trigger failed'}
        </p>
      ) : null}
      {runsQuery.isError ? (
        <p className="text-xs text-red-500 mb-2">Failed to load sweep runs.</p>
      ) : null}
      {latest ? (
        <div className="text-xs border-t border-gray-100 dark:border-gray-800 pt-1.5 space-y-1">
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-300">latest sweep</span>
            <span className={STATE_COLORS[latest.state] ?? 'text-gray-400'}>{latest.state}</span>
          </div>
          {summary ? (
            <div className="text-gray-500 dark:text-gray-400">
              {summary.runsEnqueued} stores enqueued
              {summary.storesDeferred > 0 ? (
                <span className="text-amber-600">
                  {' '}
                  · {summary.storesDeferred} deferred (run again to cover the rest)
                </span>
              ) : summary.runsEnqueued === 0 ? (
                <span> · all stores already covered</span>
              ) : null}
              {summary.scanErrors + summary.enqueueErrors > 0 ? (
                <span className="text-red-500">
                  {' '}
                  · {summary.scanErrors} scan / {summary.enqueueErrors} enqueue errors
                </span>
              ) : null}
            </div>
          ) : null}
          {latest.failedReason ? <span className="text-red-500">{latest.failedReason}</span> : null}
        </div>
      ) : (
        <p className="text-xs text-gray-400">No sweeps yet.</p>
      )}
    </div>
  );
}
